import { Badge, Card } from "@vb/ui";
import { Seo } from "../../lib/seo.js";
import { Section, SectionHeading } from "../../components/Section.js";

const paragraph = { margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 } as const;
const subhead = { margin: "0 0 6px", fontSize: 16, color: "var(--fg-1)" } as const;
const link = { color: "var(--cyan)" } as const;
const listStyle = {
  margin: 0,
  paddingLeft: 20,
  fontSize: 14,
  color: "var(--fg-2)",
  lineHeight: 1.6,
  display: "grid",
  gap: 6,
} as const;

export function Privacy() {
  return (
    <>
      <Seo
        title="Privacy · Vault Breaker Studios"
        description="GDPR-compliant privacy notice for Vault Breaker Studios — what we collect, why, who we share it with, and how to exercise your rights."
        path="/legal/privacy"
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          LEGAL
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Privacy
        </h1>
        <p style={{ fontSize: 17, color: "var(--fg-2)", marginTop: 14, maxWidth: 720, lineHeight: 1.55 }}>
          This notice explains what personal data Vault Breaker Studios collects, why we
          collect it, who we share it with, and the rights you have under the UK GDPR and
          EU GDPR. It applies to this marketing site, the <code>/play</code> demo, and the
          operator-facing surfaces (<code>/operator</code>, <code>/admin</code>).
        </p>
      </Section>

      <Section>
        <SectionHeading eyebrow="1. Controller" title="Who is responsible for your data" />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={paragraph}>
            The data controller is <strong>Vault Breaker Studios Ltd</strong> (incorporation
            in progress). General and privacy contact:{" "}
            <a href="mailto:hello@vaultbreaker.dev" style={link}>
              hello@vaultbreaker.dev
            </a>
            .
          </p>
          <p style={paragraph}>
            {"// TODO: replace with registered office once incorporated"}
          </p>
          <p style={paragraph}>
            We have not appointed a Data Protection Officer — we are not required to under
            UK GDPR Art. 37. We have not yet appointed an Art. 27 representative in the EU
            or UK. Until that is in place, the controller email above serves for all data
            protection enquiries.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="2. What we collect" title="Categories of personal data" />
        <Card style={{ display: "grid", gap: 18 }}>
          <div>
            <h3 style={subhead}>Demo users (/play)</h3>
            <ul style={listStyle}>
              <li>IP address — captured in Vercel and Fly.io edge/server logs.</li>
              <li>
                Vercel writes standard hosting access logs (IP, path, user agent) retained
                for debugging — see Vercel's privacy policy.
              </li>
              <li>
                An ephemeral demo player id (<code>demo_xxxxxx</code>) generated server-side.
                It is not linked to a real identity, and it disappears when the session
                idles out.
              </li>
            </ul>
          </div>
          <div>
            <h3 style={subhead}>Contact form submitters</h3>
            <ul style={listStyle}>
              <li>Name and work email.</li>
              <li>Operator / brand name.</li>
              <li>Country.</li>
              <li>Volume tier, integration timeline, referral source.</li>
              <li>Optional payout wallet address.</li>
              <li>Free-text message.</li>
              <li>
                Technical metadata: submission IP, UTM parameters, referrer, submission
                timestamp.
              </li>
            </ul>
          </div>
          <div>
            <h3 style={subhead}>Operator users (/operator, /admin)</h3>
            <ul style={listStyle}>
              <li>API key issued to the operator.</li>
              <li>
                Operator metadata: legal entity, jurisdictions, billing contact, technical
                contact.
              </li>
              <li>
                Round logs: session id, internal player id (as supplied by the operator,
                not a real identity from us), bet, multiplier, outcome, payout, server and
                client seeds.
              </li>
              <li>Settlement records and invoicing data.</li>
            </ul>
          </div>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="3. Why we process it"
          title="Lawful basis under Art. 6 GDPR"
        />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={paragraph}>
            <strong>Demo (/play): </strong>
            legitimate interest under Art. 6(1)(f) — presenting our product to prospective
            B2B customers and protecting the demo from abuse. We have a Legitimate
            Interests Assessment (LIA) on file; you can request a summary by email.
          </p>
          <p style={paragraph}>
            <strong>Contact form: </strong>
            legitimate interest under Art. 6(1)(f) — responding to a commercial inquiry
            you initiated. The submitter has a reasonable expectation of being contacted
            about their request.
          </p>
          <p style={paragraph}>
            <strong>Operator data: </strong>
            necessary for the performance of a contract under Art. 6(1)(b) — the
            executed Master Services Agreement between the studio and the operator.
            Round-by-round logs are also retained to comply with legal obligations
            applicable to the operator's licence (Art. 6(1)(c)).
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="4. Sub-processors"
          title="Who else touches the data"
          lead="We share personal data with the following processors under written agreements (Art. 28 GDPR)."
        />
        <Card style={{ display: "grid", gap: 14 }}>
          <ul style={listStyle}>
            <li>
              <strong>Vercel Inc.</strong> (USA) — web hosting and log infrastructure.
              Privacy policy:{" "}
              <a
                href="https://vercel.com/legal/privacy-policy"
                target="_blank"
                rel="noreferrer"
                style={link}
              >
                vercel.com/legal/privacy-policy
              </a>
              . Standard Contractual Clauses (SCCs) in place for transfers to the USA.
            </li>
            <li>
              <strong>Fly.io (Hydrobyte Inc.)</strong> (USA) — game server hosting.
              Privacy policy:{" "}
              <a
                href="https://fly.io/legal/privacy-policy"
                target="_blank"
                rel="noreferrer"
                style={link}
              >
                fly.io/legal/privacy-policy
              </a>
              . SCCs in place.
            </li>
          </ul>
          <p style={paragraph}>
            We do not sell personal data, do not share it with advertisers, and do not run
            ad-tech pixels.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="5. International transfers"
          title="Where the data goes"
        />
        <Card>
          <p style={paragraph}>
            Both sub-processors above are based in the United States. Personal data
            transferred to the USA is safeguarded under the European Commission's Standard
            Contractual Clauses (Module 2 / Module 3 as applicable), supplemented by the
            UK Addendum where the data originates in the UK. We rely on the EU-US Data
            Privacy Framework where the processor is certified, as a complementary layer.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="6. Retention" title="How long we keep it" />
        <Card style={{ display: "grid", gap: 12 }}>
          <ul style={listStyle}>
            <li>
              <strong>Demo session logs:</strong> 30 days, then aggregated or deleted.
            </li>
            <li>
              <strong>Contact form submissions:</strong> 24 months from last activity, then
              deleted unless an active commercial relationship exists.
            </li>
            <li>
              <strong>Operator records (round logs, settlement, invoices):</strong>{" "}
              duration of the contract plus 7 years, to satisfy the audit and tax
              obligations that flow through to us from operator licences.
            </li>
            <li>
              <strong>Server access logs (Vercel, Fly):</strong> per the sub-processor's
              standard retention, typically 30–90 days.
            </li>
          </ul>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="7. Your rights"
          title="What you can ask us to do"
          lead="Under UK GDPR / EU GDPR you have the following rights, free of charge:"
        />
        <Card style={{ display: "grid", gap: 12 }}>
          <ul style={listStyle}>
            <li>
              <strong>Access</strong> — a copy of the personal data we hold about you.
            </li>
            <li>
              <strong>Rectification</strong> — correction of inaccurate or incomplete data.
            </li>
            <li>
              <strong>Erasure</strong> — deletion where there is no overriding lawful basis
              (note: regulator-mandated audit retention may override this for round logs).
            </li>
            <li>
              <strong>Restriction</strong> — pause processing while a dispute is resolved.
            </li>
            <li>
              <strong>Portability</strong> — receive a structured, machine-readable export.
            </li>
            <li>
              <strong>Objection</strong> — to processing based on legitimate interest.
            </li>
            <li>
              <strong>Complaint</strong> — to a supervisory authority. Once the entity is
              registered our lead supervisory authority will be the UK Information
              Commissioner's Office (
              <a
                href="https://ico.org.uk/"
                target="_blank"
                rel="noreferrer"
                style={link}
              >
                ico.org.uk
              </a>
              ); EU residents may also complain to their national DPA via the EDPB.
            </li>
          </ul>
          <p style={paragraph}>
            To exercise any of these, email{" "}
            <a href="mailto:hello@vaultbreaker.dev" style={link}>
              hello@vaultbreaker.dev
            </a>
            . We respond within 30 days (extendable by a further two months for complex
            requests, with notice). Operator end-user requests should normally be routed
            through the operator first; if the operator hands the request off to us we
            will action it within the same window.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="8. Cookies" title="What we set in your browser" />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={paragraph}>
            We do not set any cookies on this site. We do not load Google Tag Manager,
            Segment, Hotjar, Meta Pixel, or any similar tracker.
          </p>
          <p style={paragraph}>
            If you click an outbound link to a third party — for example a sub-processor's
            website — that third party may set its own cookies under its own privacy
            policy. We do not embed such tools in-page and do not pass identifiers to them.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="9. Security" title="How we protect data" />
        <Card>
          <p style={paragraph}>
            Transport is encrypted in flight (TLS 1.2+). Operator API keys are stored
            hashed. Round seeds are committed before resolution and revealed afterwards;
            the audit trail is append-only. Access to production systems is restricted
            to the founder and is protected by hardware-key two-factor authentication. We
            will notify affected data subjects and, where required, the relevant
            supervisory authority within 72 hours of becoming aware of a notifiable
            personal data breach.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="10. Changes" title="When this notice updates" />
        <Card>
          <p style={paragraph}>
            We update this page when our processing changes — for example, when we add a
            sub-processor or change a retention period. The "Last updated" date below is
            authoritative. Material changes will be notified by email to active operator
            contacts.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="11. Contact" title="Reach the controller" />
        <Card>
          <p style={paragraph}>
            Email{" "}
            <a href="mailto:hello@vaultbreaker.dev" style={link}>
              hello@vaultbreaker.dev
            </a>
            . Please put "Privacy" in the subject line so it routes correctly.
          </p>
        </Card>
      </Section>

      <Section tight>
        <p style={{ fontSize: 13, color: "var(--fg-3)", margin: 0 }}>
          Last updated: 2026-05-05.
        </p>
      </Section>
    </>
  );
}
